import React, { type FC, useEffect, useMemo } from 'react';
import { Router } from '@funtech-int/rival';
import { useRouter } from '@funtech-int/rival-react';
import { useStorage } from '@funtech-int/web-toolkit';
import { Tooltip } from '@funtech-int/yango-shared';

import { useHasSubscription } from '@/shared/hooks/useHasSubscription';

const WAS_ANY_HEADER_HINT_SHOWED = 'was_any_header_hint_showed';

const getLocalStorageItemName = (id: string) => `${id}_nav_link_hint_showed`;

interface HeaderHintProps {
    wrapperClassname: string;
    hintClassname: string;
    isHintVisible: boolean;
    text: string;
    id: string;
    getIsHintShowedInitial: (router: Router) => boolean;
}

export const HeaderHint: FC<HeaderHintProps> = ({
    wrapperClassname,
    text,
    id,
    hintClassname,
    isHintVisible,
    getIsHintShowedInitial,
}) => {
    const router = useRouter();
    const hasSubscription = useHasSubscription();
    const storage = useStorage();

    const isHintInitialShowed = useMemo(() => {
        const isHintShowedInitial =
            !storage.session.get(WAS_ANY_HEADER_HINT_SHOWED) &&
            !storage.local.get(getLocalStorageItemName(id)) &&
            getIsHintShowedInitial(router);

        return isHintShowedInitial && hasSubscription;
    }, [getIsHintShowedInitial, hasSubscription, id, router, storage]);

    useEffect(() => {
        if (isHintInitialShowed) {
            const timer = setTimeout(() => {
                storage.session.set(WAS_ANY_HEADER_HINT_SHOWED, '1');
                storage.local.set(getLocalStorageItemName(id), '1');
            }, 1000);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHintInitialShowed]);

    const isHintShowed = isHintInitialShowed && isHintVisible;

    return (
        <div className={wrapperClassname}>
            <Tooltip
                direction="bottom"
                alignment="middle"
                className={hintClassname}
                visible={isHintShowed}
                withOuterClick={false}
            >
                {text}
            </Tooltip>
        </div>
    );
};
